import { signal } from '@angular/core';

/**
 * The crest is part of the launch, not something that arrives after it.
 *
 * A branded build's door IS its crest, so painting the door before the image
 * has decoded shows an empty box that fills in a beat later - and a
 * progressive PNG fills in visibly, line by line. Everything that reveals the
 * app waits on {@link crestReady}: the native splash (main.ts holds it) and
 * the boot gate veil (the shell folds it into `done`).
 *
 * On a flavour build there is usually nothing to wait for - the pipeline
 * bakes the crest into the bundle, so this resolves off disk in a frame. The
 * wait earns its keep for the web `/{slug}` entry, where the crest really is
 * a Firebase Storage fetch.
 *
 * It can NEVER hold the app hostage: a crest that is slow, offline or gone
 * settles at {@link CREST_TIMEOUT_MS} and the app comes up regardless. A
 * missing crest is a cosmetic problem; a launch that never finishes is not.
 *
 * The VERDICT is reported as well as the wait: everything that reveals the
 * app carries on either way, but a consumer that would rather show nothing
 * than something broken - the browser tab's icon - needs to know which it
 * was (see core/club-chrome).
 */

/** How long a crest may hold the launch before the app comes up without it. */
export const CREST_TIMEOUT_MS = 3_000;

/** Nothing to wait for until someone names a crest. */
const ready = signal(true);

/** Whether the active crest has decoded (or given up). */
export const crestReady = ready.asReadonly();

/** The crest currently awaited, so a second ask for the same one is free. */
let pending: { url: string; settled: Promise<boolean> } | null = null;

/**
 * Wait for `url` to be decoded and ready to paint. Resolves immediately when
 * there is no crest, when it is already loaded, or when the timeout is up -
 * with TRUE only when the image actually decoded, so a caller can tell a
 * crest that is ready from one that is broken or too slow to wait for.
 */
export function awaitCrest(url: string | null | undefined): Promise<boolean> {
  if (!url) {
    ready.set(true);
    pending = null;
    return Promise.resolve(false);
  }
  if (pending?.url === url) return pending.settled;

  ready.set(false);
  const settled = new Promise<boolean>((resolve) => {
    let done = false;
    const settle = (decoded: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      ready.set(true);
      resolve(decoded);
    };
    // Out of time is not a verdict on the image, but it IS a verdict on
    // waiting for it: the app comes up, and anything that would rather show
    // nothing than a half-loaded thing shows nothing.
    const timer = setTimeout(() => settle(false), CREST_TIMEOUT_MS);

    const img = new Image();
    img.src = url;
    // decode() is the whole point over onload: it resolves when the bitmap is
    // ready to PAINT, so the first frame that shows the crest shows all of
    // it. It rejects on a broken image - settle either way.
    if (typeof img.decode === 'function') {
      void img.decode().then(
        () => settle(true),
        () => settle(false),
      );
    } else {
      img.onload = () => settle(true);
      img.onerror = () => settle(false);
    }
  });

  pending = { url, settled };
  return settled;
}
